/*
 * Tona · PR-06 y PR-08 · Galería de la ficha de producto: miniaturas, contador n / N y, en
 * celular, la rayita o la fila de miniaturas.
 *
 * Vive en <tona-galeria-controles>, adentro de <media-gallery>. Horizon REEMPLAZA la galería
 * entera cuando se elige otro color (media-gallery.js → replaceWith), así que cada galería nueva
 * trae su propio elemento y se inicia sola: no hace falta escuchar el cambio de variante.
 *
 * - La foto activa sale de un IntersectionObserver sobre el MARCO (la lista de fotos, que tiene
 *   scroll propio), no sobre la ventana: la página no se mueve mientras se recorren las fotos.
 * - Clic en una miniatura: el marco va a esa foto (instantáneo con prefers-reduced-motion).
 * - No intercepta la rueda ni el dedo: el scroll es el del navegador.
 * - En el modal de compra rápida (que clona esta galería) no hace nada.
 * - PR-08: ?tona_galeria=rayita o ?tona_galeria=miniaturas en el link pisa la variante de celular
 *   elegida en el editor, para mirar las dos juntas con dos links (Horizon conserva el parámetro
 *   al cambiar de color, así que la galería nueva lo vuelve a leer).
 * - PR-57 · La columna de miniaturas ya no queda tapada por el header ni salta al hacer clic:
 *   · Tapada: en compu el header va en tarjeta, dibujada 8 px más abajo que su lugar en la página
 *     (ajuste «Espacio arriba de la tarjeta»), y --header-group-height no los cuenta. Encima ese
 *     número no es confiable: Horizon lo suma solo con lo que cambió de tamaño en cada aviso, así que
 *     en la misma ficha medimos 80 px en una carga y 50 en otra (y ahí el marco quedaba 30 px más
 *     alto que la pantalla). Ahora se mide dónde termina de verdad la tarjeta del header
 *     (--tona-ficha-techo) y cuánto hay que bajar la galería para que arranque ahí
 *     (--tona-ficha-bajar). Solo desde el corte de compu: en celular la galería pasa por detrás.
 *     PR-77: las dos quedan en la grilla de la ficha (.product-information__grid), así el panel de compra
 *     arranca a la misma altura con la misma medida, y se les suma el ajuste «Aire debajo del header en
 *     compu» de la sección (data-tona-aire).
 *   · Salto: al tocar una miniatura el marco se desliza hasta esa foto y, en el camino, el
 *     IntersectionObserver marcaba cada foto intermedia y cada marca movía la columna (medido: al
 *     tocar la 8.ª, la columna pasó por 20 posiciones y marcó 1, 2, 3… 7). Ahora, mientras el marco
 *     viaja, no se marca nada, y la columna no se mueve: la miniatura tocada ya está a la vista.
 *     Cuando las fotos se recorren con la rueda, la columna se mueve lo justo para mostrar la activa,
 *     y solo si no se ve entera.
 * - PR-58 · Con «Miniaturas en compu» = «Contra el margen de la página», la columna arranca en la línea
 *   donde arranca el contenido del header (el borde izquierdo del logo). Esa distancia contra el borde
 *   de la galería se mide acá (--tona-logo-x): depende de los ajustes del header (tarjeta, márgenes),
 *   que la galería no puede leer desde el CSS.
 * - PR-65 · En celular, con «En celular, la foto pasa detrás del header», se mide cuánto mide el header
 *   (de la marquesina a la galería) y la galería sube eso por detrás (--tona-galeria-subir). Solo si la
 *   galería arranca pegada al header: si hubiera otra sección en el medio, no se tapa nada.
 */
(() => {
  if (customElements.get('tona-galeria-controles')) return;

  const movimientoReducido = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class TonaGaleriaControles extends HTMLElement {
    connectedCallback() {
      this.intentos = 0;
      this.iniciar();
      window.addEventListener('resize', this.alCambiarTamano, { passive: true });
      document.addEventListener('shopify:section:load', this.alCambiarTamano);
    }

    disconnectedCallback() {
      this.observador?.disconnect();
      this.observadorPanel?.disconnect();
      this.zoom?.removeEventListener('zoom-media:selected', this.alElegirEnZoom);
      this.removeEventListener('click', this.alHacerClic);
      window.removeEventListener('resize', this.alCambiarTamano);
      document.removeEventListener('shopify:section:load', this.alCambiarTamano);
      this.soltarViaje?.();
      clearTimeout(this.relojViaje);
    }

    iniciar() {
      this.galeria = this.closest('media-gallery');
      if (!this.galeria || !this.closest('.kaboa-product-information')) return;

      const variante = new URLSearchParams(window.location.search).get('tona_galeria');
      if (variante === 'rayita' || variante === 'miniaturas') this.galeria.dataset.tonaCelular = variante;

      this.lista = this.galeria.querySelector(':scope > .media-gallery__grid');
      if (!this.lista) {
        // La lista va después de este elemento en el HTML: si todavía no está, se reintenta.
        if (this.intentos++ < 20) requestAnimationFrame(() => this.iniciar());
        return;
      }

      this.fotos = Array.from(this.lista.children);
      this.miniaturas = Array.from(this.querySelectorAll('.tona-galeria__miniatura'));
      this.segmentos = Array.from(this.querySelectorAll('.tona-galeria__segmento'));
      this.columna = this.querySelector('.tona-galeria__miniaturas');
      this.numero = this.querySelector('[data-tona-actual]');
      this.actual = -1;
      this.proporciones = new Map();
      this.destino = null;
      this.compu = window.matchMedia(`(min-width: ${Number(this.galeria.dataset.tonaCorte) || 990}px)`);

      this.medirTecho();

      this.observador?.disconnect();
      this.observador = new IntersectionObserver(this.alCruzar, {
        root: this.lista,
        threshold: [0, 0.25, 0.5, 0.75, 1],
      });
      this.fotos.forEach((foto) => this.observador.observe(foto));

      this.addEventListener('click', this.alHacerClic);

      // Si se recorrieron fotos adentro del zoom, al cerrarlo el marco queda en la misma foto.
      this.zoom = this.galeria.querySelector('zoom-dialog');
      this.zoom?.addEventListener('zoom-media:selected', this.alElegirEnZoom);

      this.marcar(0);
    }

    alCruzar = (entradas) => {
      for (const entrada of entradas) this.proporciones.set(entrada.target, entrada.intersectionRatio);
      // PR-57: mientras el marco viaja hacia la foto tocada, las fotos del camino no se marcan.
      if (this.destino === null) this.marcarLaMasVisible(true);
    };

    marcarLaMasVisible(seguir) {
      let elegida = -1;
      let mayor = 0;
      this.fotos.forEach((foto, indice) => {
        const proporcion = this.proporciones.get(foto) || 0;
        if (proporcion > mayor + 0.001) {
          mayor = proporcion;
          elegida = indice;
        }
      });

      if (elegida >= 0) this.marcar(elegida, seguir);
    }

    alHacerClic = (evento) => {
      const boton = evento.target.closest('.tona-galeria__miniatura');
      if (!boton || !this.contains(boton)) return;
      const indice = Number(boton.dataset.indice);
      // PR-57: se marca ya y la columna queda quieta (la miniatura tocada está a la vista).
      this.marcar(indice, false);
      this.viajar(indice);
      this.ir(indice, movimientoReducido() ? 'instant' : 'smooth');
    };

    /**
     * PR-57 · Traba las marcas mientras el marco se desliza hasta `indice`. Se destraba cuando el
     * scroll termina (scrollend, o 150 ms sin moverse donde no existe), o apenas la persona toma el
     * control con la rueda, el dedo o el teclado. Al destrabar se marca la foto que quedó a la vista,
     * sin mover la columna.
     */
    viajar(indice) {
      this.soltarViaje?.();
      this.destino = indice;
      const lista = this.lista;
      const soltar = () => {
        clearTimeout(this.relojViaje);
        lista.removeEventListener('scroll', alMover);
        lista.removeEventListener('scrollend', soltar);
        ['wheel', 'touchstart', 'keydown'].forEach((tipo) => lista.removeEventListener(tipo, soltar));
        this.soltarViaje = null;
        if (this.destino === null) return;
        this.destino = null;
        this.marcarLaMasVisible(false);
      };
      const alMover = () => {
        clearTimeout(this.relojViaje);
        this.relojViaje = setTimeout(soltar, 150);
      };
      lista.addEventListener('scroll', alMover, { passive: true });
      lista.addEventListener('scrollend', soltar);
      ['wheel', 'touchstart', 'keydown'].forEach((tipo) => lista.addEventListener(tipo, soltar, { passive: true }));
      // Si el marco ya estaba en esa foto no hay scroll: se destraba solo.
      this.relojViaje = setTimeout(soltar, 400);
      this.soltarViaje = soltar;
    }

    /**
     * PR-57 · Dónde termina de verdad el header (la tarjeta de vidrio, no su lugar en la página) y cuánto
     * hay que bajar la galería para arrancar ahí. PR-58 · Y dónde arranca su contenido (el borde izquierdo
     * del logo), para la columna «contra el margen». Solo en compu; abajo del corte se sacan y la galería
     * queda como estaba.
     */
    medirTecho() {
      const galeria = this.galeria;
      if (!galeria) return;
      const tarjeta = document.querySelector('#header-component .tona-hdr__barra');
      const caja = tarjeta?.getBoundingClientRect();
      // PR-77: la medida es de la ficha entera (fotos, miniaturas y panel): va en su grilla.
      const ficha = galeria.closest('.product-information__grid') || galeria;
      if (!caja || caja.height === 0 || !this.compu?.matches) {
        ['--tona-ficha-techo', '--tona-ficha-bajar', '--tona-panel-centro'].forEach((v) => ficha.style.removeProperty(v));
        galeria.style.removeProperty('--tona-logo-x');
        this.medirSubida();
        return;
      }
      galeria.style.removeProperty('--tona-galeria-subir');
      const aire = Math.max(0, Number(galeria.dataset.tonaAire) || 0);
      const techo = Math.max(0, Math.round(caja.bottom)) + aire;
      const bajadaActual = parseFloat(getComputedStyle(galeria).marginTop) || 0;
      const lugar = galeria.getBoundingClientRect();
      const arriba = lugar.top + window.scrollY - bajadaActual;
      ficha.style.setProperty('--tona-ficha-techo', techo + 'px');
      ficha.style.setProperty('--tona-ficha-bajar', Math.max(0, Math.ceil(techo - arriba)) + 'px');
      this.medirPanel(ficha, techo);

      const fila = tarjeta.querySelector('.tona-hdr__fila') || tarjeta;
      const bordeContenido = fila.getBoundingClientRect().left + (parseFloat(getComputedStyle(fila).paddingLeft) || 0);
      galeria.style.setProperty('--tona-logo-x', Math.max(0, Math.round(bordeContenido - lugar.left)) + 'px');
    }

    /**
     * PR-77 (2.ª vuelta) · El panel de compra centrado en la pantalla, a la par de la foto y de las miniaturas
     * (Tona: «tiene que quedar alineado central verticalmente»). Se baja la mitad de lo que sobra entre el header y
     * el pie de la pantalla; si el panel no entra, no se baja nada y se recorre adentro. Se vuelve a medir cuando el
     * panel cambia de alto (un desplegable que se abre, otro color, otro talle).
     */
    medirPanel(ficha, techo) {
      const panel = ficha.querySelector(':scope > .kaboa-product-details-island');
      if (!panel || (this.galeria.dataset.tonaPanel || 'centro') !== 'centro') {
        ficha.style.removeProperty('--tona-panel-centro');
        return;
      }
      const alto = panel.scrollHeight;
      const sobra = window.innerHeight - techo - alto;
      ficha.style.setProperty('--tona-panel-centro', Math.max(0, Math.floor(sobra / 2)) + 'px');
      if (!this.observadorPanel && window.ResizeObserver) {
        this.observadorPanel = new ResizeObserver(() => this.alCambiarTamano());
        this.observadorPanel.observe(panel.firstElementChild || panel);
      }
    }

    /** PR-65 · Celular: cuánto sube el fondo de la galería por detrás del header (ver el comentario de arriba). */
    medirSubida() {
      const galeria = this.galeria;
      const header = document.getElementById('header-component');
      if (!galeria.hasAttribute('data-tona-bajo-header') || !header) {
        galeria.style.removeProperty('--tona-galeria-subir');
        return;
      }
      const subidaActual = parseFloat(galeria.style.getPropertyValue('--tona-galeria-subir')) || 0;
      const arriba = galeria.getBoundingClientRect().top + window.scrollY + subidaActual;
      const lugarHeader = header.getBoundingClientRect();
      const pegada = Math.abs(arriba - lugarHeader.bottom) <= 2;
      const subir = pegada ? Math.max(0, Math.round(arriba - lugarHeader.top)) : 0;
      galeria.style.setProperty('--tona-galeria-subir', subir + 'px');
    }

    alCambiarTamano = () => {
      if (this.esperandoMedida) return;
      this.esperandoMedida = true;
      requestAnimationFrame(() => {
        this.esperandoMedida = false;
        this.medirTecho();
      });
    };

    alElegirEnZoom = (evento) => {
      const indice = evento.detail?.index;
      if (typeof indice === 'number') this.ir(indice, 'instant');
    };

    /**
     * Lleva la foto `indice` a la vista: el marco vertical de compu, el carrusel horizontal de celular
     * o, si la lista no tiene scroll propio (fotos apiladas en la página), la página misma.
     *
     * ⚠️ PR-37: antes el eje salía de `flex-direction`, y eso FALLA EN SILENCIO. En una lista que no
     * es flex —la grilla apilada de Horizon— getComputedStyle devuelve igual 'row', así que el clic
     * movía la lista a lo ancho, donde no hay nada que mover, y no pasaba nada ni saltaba ningún error.
     * Ahora se mira quién puede desplazarse de verdad, que es el dato que importa.
     */
    ir(indice, comportamiento) {
      const foto = this.fotos[indice];
      if (!foto) return;
      const lista = this.lista;
      const desbordeX = lista.scrollWidth - lista.clientWidth;
      const desbordeY = lista.scrollHeight - lista.clientHeight;

      if (desbordeX > 1 && desbordeX >= desbordeY) {
        lista.scrollTo({ left: foto.offsetLeft, behavior: comportamiento });
      } else if (desbordeY > 1) {
        lista.scrollTo({ top: foto.offsetTop, behavior: comportamiento });
      } else {
        // PR-57: la altura del header vive en el <body> (antes se leía del <html> y daba 0); si ya se
        // midió la tarjeta, se usa esa, que es la que se ve.
        const techo =
          parseFloat(getComputedStyle(this.galeria).getPropertyValue('--tona-ficha-techo')) ||
          parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height')) ||
          0;
        window.scrollTo({ top: foto.getBoundingClientRect().top + window.scrollY - techo, behavior: comportamiento });
      }
    }

    /** `seguir` = mover la columna para que se vea la miniatura activa (no, cuando la tocaron). */
    marcar(indice, seguir = true) {
      if (indice === this.actual) return;
      this.actual = indice;

      this.miniaturas.forEach((boton, i) => {
        if (i === indice) boton.setAttribute('aria-current', 'true');
        else boton.removeAttribute('aria-current');
      });

      this.segmentos.forEach((segmento, i) => segmento.toggleAttribute('data-activo', i === indice));

      if (this.numero) this.numero.textContent = String(indice + 1);
      this.galeria.style.setProperty('--tona-actual', String(indice));

      if (seguir) this.mostrarMiniatura(this.miniaturas[indice]);
    }

    /**
     * Si la columna de miniaturas tiene scroll y la activa no se ve entera, la columna se mueve LO JUSTO
     * para mostrarla (PR-57: antes la centraba siempre, y eso era el salto). Nunca mueve la página.
     */
    mostrarMiniatura(boton) {
      const columna = this.columna;
      if (!boton || !columna) return;
      const comportamiento = movimientoReducido() ? 'instant' : 'smooth';
      const borde = 4;

      if (columna.scrollHeight > columna.clientHeight + 1) {
        const arriba = boton.offsetTop - borde;
        const abajo = boton.offsetTop + boton.offsetHeight + borde - columna.clientHeight;
        if (arriba < columna.scrollTop) columna.scrollTo({ top: Math.max(0, arriba), behavior: comportamiento });
        else if (abajo > columna.scrollTop) columna.scrollTo({ top: abajo, behavior: comportamiento });
      } else if (columna.scrollWidth > columna.clientWidth + 1) {
        const izquierda = boton.offsetLeft - borde;
        const derecha = boton.offsetLeft + boton.offsetWidth + borde - columna.clientWidth;
        if (izquierda < columna.scrollLeft) columna.scrollTo({ left: Math.max(0, izquierda), behavior: comportamiento });
        else if (derecha > columna.scrollLeft) columna.scrollTo({ left: derecha, behavior: comportamiento });
      }
    }
  }

  customElements.define('tona-galeria-controles', TonaGaleriaControles);
})();
